import React, { useState, useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'

// Styles
import { applyTextStyle } from '../styles/typography'
import { fastTransition, shadows } from '../styles/effects'

// Components
import { Icon } from './icon'

// Types
import { AnyColor } from '../common/models/colors'
import { IconType } from './icon/IconType'

export enum ButtonVariant {
  Filled = 'filled',
  Text = 'text',
  Outlined = 'outlined', // Meaning: text & border in the same color (currently unused)
}

export type ColorVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  // 'secondary' with its icon muted until hovered, for the buttons standing
  // beside the one action a row of actions leads with. Same fill, so the row
  // reads as one block and only the lead action carries colour.
  | 'quiet'

/** Small is the row-of-actions size: 24px tall, usually carrying no label. */
export type ButtonSize = 'small'

export interface IProps {
  disabled?: boolean
  caption?: string | React.ReactNode
  hoverCaption?: string | React.ReactNode
  /** The name a button with no caption goes by — an icon has none of its own. */
  ariaLabel?: string
  icon?: IconType
  variant?: ButtonVariant
  colorVariant?: ColorVariant
  color?: AnyColor
  textColor?: AnyColor
  round?: boolean
  size?: ButtonSize
  hoverColor?: AnyColor
  hoverTextColor?: AnyColor
  switchOrder?: boolean
  className?: string
  withShadow?: boolean
  iconSize?: number
  iconVariant?: boolean
  overrideOnHover?: boolean
  onClick?: (event?: React.MouseEvent) => void
  id?: string
  // Cypress handle, forwarded to the rendered button. See cypress/README.md.
  'data-cy'?: string
  anchor?: {
    href: string
    target?: string
  }
}

const getButtonColors: (
  variant: ColorVariant
) => {
  fill: AnyColor
  text: AnyColor
  hoverFill: AnyColor
  hoverText: AnyColor
} = (variant) => {
  switch (variant) {
    case 'primary':
    default:
      return {
        fill: 'primaryFadeSemi',
        text: 'lightest',
        hoverFill: 'primary',
        hoverText: 'lightest',
      }
    case 'secondary':
      return {
        fill: 'primaryFadeLight',
        text: 'primary',
        hoverFill: 'primary',
        hoverText: 'lightest',
      }
    case 'tertiary':
      return {
        fill: 'lightest',
        text: 'primary',
        hoverFill: 'primaryFadeLight',
        hoverText: 'primary',
      }
    case 'quaternary':
      return {
        fill: 'textLightest',
        text: 'primary',
        hoverFill: 'primaryFadeSemi',
        hoverText: 'lightest',
      }
    case 'quiet':
      return {
        fill: 'primaryFadeLight',
        text: 'textLighter',
        hoverFill: 'primaryFadeLight',
        hoverText: 'primary',
      }
  }
}

const Button = (props: IProps) => {
  const themeContext = useContext(ThemeContext)
  const [isHovered, setIsHovered] = useState<boolean>(false)

  const caption = props.caption || ''
  const hoverCaption = props.hoverCaption || caption
  const color = props.colorVariant
    ? getButtonColors(props.colorVariant).fill
    : props.color || 'secondary'
  const textColor = props.colorVariant
    ? getButtonColors(props.colorVariant).text
    : props.textColor || 'lightest'
  const hoverColor = props.colorVariant
    ? themeContext.colors[`${getButtonColors(props.colorVariant).hoverFill}`]
    : props.hoverColor
    ? themeContext.colors[`${props.hoverColor}`]
    : themeContext.colors[`${props.color || `secondary`}Dark`]
  const hoverTextColor = props.colorVariant
    ? getButtonColors(props.colorVariant).hoverText
    : props.hoverTextColor || 'lightest'
  const variant = props.variant ? props.variant : ButtonVariant.Filled
  const iconColor = props.colorVariant
    ? themeContext.colors[textColor]
    : props.textColor
    ? themeContext.colors[props.textColor]
    : variant === ButtonVariant.Filled
    ? themeContext.colors.lightest
    : themeContext.colors[color]
  const iconSize = props.iconSize ? props.iconSize : 14
  const id = props.id
  const overrideOnHover = props.overrideOnHover ?? true

  return props.anchor ? (
    <StyledButton
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={props.onClick}
      className={props.className}
      color={color}
      variant={variant}
      round={props.round}
      $size={props.size}
      as="a"
      textColor={textColor}
      hoverColor={hoverColor}
      hoverTextColor={hoverTextColor}
      id={id}
      data-cy={props['data-cy']}
      aria-label={props.ariaLabel}
      href={props.anchor.href}
      target={props.anchor.target}
    >
      {props.icon !== undefined &&
        (!overrideOnHover || (props.hoverCaption ? !isHovered : true)) && (
          <Icon
            size={iconSize}
            color={props.disabled ? themeContext.colors.lightest : iconColor}
            type={props.icon}
            variant={props.iconVariant}
          />
        )}
      {!!caption && (
        <ButtonLabel switchOrder={props.switchOrder}>
          {isHovered ? hoverCaption : caption}
        </ButtonLabel>
      )}
    </StyledButton>
  ) : (
    <StyledButton
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={props.onClick}
      className={props.className}
      disabled={props.disabled}
      color={color}
      variant={variant}
      round={props.round}
      $size={props.size}
      type="button"
      textColor={textColor}
      hoverColor={hoverColor}
      withShadow={props.withShadow}
      hoverTextColor={hoverTextColor}
      colorVariant={props.colorVariant}
      id={id}
      aria-label={props.ariaLabel}
      data-cy={props['data-cy']}
    >
      {props.icon !== undefined &&
        (!overrideOnHover || (props.hoverCaption ? !isHovered : true)) && (
          <Icon
            size={iconSize}
            color={props.disabled ? themeContext.colors.text72 : iconColor}
            type={props.icon}
          />
        )}
      {!!caption && (
        <ButtonLabel switchOrder={props.switchOrder}>
          {isHovered ? hoverCaption : caption}
        </ButtonLabel>
      )}
    </StyledButton>
  )
}

type IStyledButtonProps = { $size?: ButtonSize } & Pick<
  IProps,
  | 'variant'
  | 'color'
  | 'round'
  | 'textColor'
  | 'hoverColor'
  | 'hoverTextColor'
  | 'colorVariant'
  | 'withShadow'
>

export const StyledButton = styled.button<IStyledButtonProps>`
  align-items: center;
  justify-content: center;
  display: flex;
  padding: ${({ $size }) => ($size === 'small' ? '4px 8px' : '8px 12px')};
  border: 0;
  cursor: pointer;
  transition: color ${fastTransition}, background-color ${fastTransition},
    box-shadow ${fastTransition}, outline-color ${fastTransition};
  outline: 0px solid transparent;
  outline-offset: -2px;
  box-shadow: ${({ colorVariant, withShadow }) =>
    colorVariant === 'quaternary' || withShadow ? shadows.depthOne : 'none'};

  &:disabled {
    cursor: default;
    pointer-events: none;
    box-shadow: none;
    label {
      cursor: default;
    }
  }
  ${(props) =>
    props.variant === 'filled' &&
    css`
      border-radius: 8px;
      /* A labelled button is as tall as its 16px line box; an icon-only one
         would follow its 14px icon and come out 2px short. This keeps every
         filled button of a size on the same height, labelled or not. */
      min-height: ${props.$size === 'small' ? 24 : 32}px;
      color: ${props.theme.colors[props.textColor!]};
      background-color: ${props.theme.colors[props.color!]};

      &:hover {
        box-shadow: none;
        background-color: ${props.hoverColor};
        color: ${props.theme.colors[props.hoverTextColor!]};
        svg > path {
          fill: ${props.theme.colors[props.hoverTextColor!]};
        }
      }
      &:disabled {
        background-color: ${props.theme.colors.textLightest};
        color: ${props.theme.colors.text72};
      }
    `}
  ${(props) =>
    props.variant === 'text' &&
    css`
      color: ${props.theme.colors[props.color!]};
      background-color: transparent;
      padding: 0px;

      &:hover {
        box-shadow: none;
        color: ${props.hoverColor};
        svg > path {
          fill: ${props.hoverColor};
        }
      }
      &:disabled {
        color: ${props.theme.colors.dark};
        svg > path {
          fill: ${props.theme.colors.dark};
        }
      }
    `}

    > span {
    order: 1;
  }

  ${({ round }) =>
    round &&
    `
    border-radius: 50%;
  `}
`

const ButtonLabel = styled.label<{ switchOrder?: boolean }>`
  ${applyTextStyle('labelHeavy')}
  cursor: pointer;
  order: ${({ switchOrder }) => (switchOrder ? 0 : 2)};

  ${({ switchOrder }) => switchOrder && `margin-right: 8px;`}
`
export default Button
